/**
 *
 * QuestionImagePicker
 *
 */

import React from 'react';
import PropTypes from 'prop-types';
import _classNames from 'classnames';
import { useSelector } from 'react-redux';
import _get from 'lodash/get';
import _isNil from 'lodash/isNil';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { makeSelectClientDetails } from 'containers/Main/selectors';
import useSiteConfigSelector from 'components/useSiteConfigSelector';
import AllImage from './images/all.svg';
import NoneImage from './images/none.svg';

const useStyles = makeStyles(theme => ({
  container: {
    marginTop: 16,
  },
  wrapper: {
    cursor: 'pointer',
  },
  imageContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
  imageWrapper: {
    display: 'flex',
    width: '100%',
    maxWidth: 80,
    height: '100%',
    maxHeight: 80,
    position: 'relative',
    borderRadius: '50%',
    border: '2px solid transparent',
    '&:hover': {
      opacity: 0.8,
    },
  },
  imageWrapperSelected: {
    borderColor: '#EE9F00',
  },
  image: {
    width: '100%',
    height: '100%',
    borderRadius: '50%',
    objectFit: ({ imageFit }) => imageFit,
    border: '2px solid transparent',
  },
  defaultImage: {
    padding: '10%',
    borderColor: '#F1F1F1',
    backgroundColor: '#FFFFFF',
    borderRadius: '50%',
  },
  imageSelected: {
    borderColor: '#FFFFFF',
  },
  icon: {
    position: 'absolute',
    fontSize: 20,
    color: '#EE9F00',
    right: 0,
    bottom: 0,
    margin: 'auto',
    backgroundColor: '#FAFAFA',
    borderRadius: 10,
  },
  label: {
    fontFamily: 'PoppinsRegular',
    textAlign: 'center',
    fontSize: 12,
    textTransform: 'uppercase',
    lineHeight: '1.4',
    color: theme.palette.text.primary,
  },
  labelSelected: {
    color: '#EE9F00',
  },
}));

function QuestionImagePicker(props) {
  const {
    name,
    choices,
    answers,
    multiSelect,
    imageFit,
    imageWidth,
    showLabel,
    onChange,
    onCheckboxChange,
  } = props;
  const classes = useStyles({ imageFit, imageWidth });

  const clientDetails = useSelector(makeSelectClientDetails());
  const clientExcludedTopicsSlugs = (
    _get(clientDetails, 'excludeTopicCollection.items') || []
  ).map(topic => topic.slug);
  const [siteConfig] = useSiteConfigSelector(['Assessment Approach Lists']);
  const approachesQuestionNames = Object.keys(siteConfig.config)
    .map(key => _get(siteConfig.config, [key, 'question']))
    .filter(questionName => !_isNil(questionName));

  const finalChoices = approachesQuestionNames.includes(name)
    ? choices.filter(
        choice => !clientExcludedTopicsSlugs.includes(choice.value),
      )
    : choices;

  const onClickCallback = multiSelect ? onCheckboxChange : onChange;
  const normalizeAnswers = (multiSelect ? answers : [answers]) || [];
  return (
    <Grid container spacing={4} className={classes.container}>
      {finalChoices.map(option => (
        <Grid
          key={option.value}
          item
          xs={6}
          sm={4}
          md={3}
          className={classes.wrapper}
          onClick={() => onClickCallback(name, option.value)}
        >
          <Grid container spacing={1}>
            <Grid item xs={12} className={classes.imageContainer}>
              <Box
                component="div"
                className={_classNames({
                  [classes.imageWrapper]: true,
                  [classes.imageWrapperSelected]: normalizeAnswers.includes(
                    option.value,
                  ),
                })}
              >
                <img
                  src={
                    option.value === 'none'
                      ? NoneImage
                      : option.value === 'all'
                      ? AllImage
                      : option.imageLink
                  }
                  alt={`for ${option.value} option`}
                  className={_classNames({
                    [classes.image]: true,
                    [classes.defaultImage]:
                      option.value === 'none' || option.value === 'all',
                    [classes.imageSelected]: normalizeAnswers.includes(
                      option.value,
                    ),
                  })}
                />
                {normalizeAnswers.includes(option.value) && (
                  <CheckCircleIcon className={classes.icon} />
                )}
              </Box>
            </Grid>
            {showLabel && (
              <Grid item xs={12}>
                <Typography
                  className={_classNames({
                    [classes.label]: true,
                    [classes.labelSelected]: normalizeAnswers.includes(
                      option.value,
                    ),
                  })}
                >
                  {option.text}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      ))}
    </Grid>
  );
}

QuestionImagePicker.propTypes = {
  name: PropTypes.string,
  choices: PropTypes.array,
  answers: PropTypes.any,
  multiSelect: PropTypes.bool,
  imageFit: PropTypes.string,
  imageWidth: PropTypes.number,
  showLabel: PropTypes.bool,
  onChange: PropTypes.func,
  onCheckboxChange: PropTypes.func,
};

export default QuestionImagePicker;
